<script>
	import { getSizeInBytes, slugify, watchObject } from "cando-util";
	import Dropzone from "svelte-file-dropzone";
	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
	import { formatFileSize } from "$lib/utilities/formatFileSize";
	import { uploadFiles } from "$lib/sections/FormSubmission/uploadFiles.js";
	import ImageComponent from "$lib/components/Image.svelte";

	export let value;
	export let question;
	export let handleChange = () => {};
	export let readonly = false;
	export let label;
	export let accept;
	export let action;
	export let docId;
	export let col;
	export let key;
	export let existingDoc;
	export let excludePath = true;

	let fileNotAccepted;
	let progress = 0;
	let loading = false;

	const handleFilesSelect = async (e, question) => {
		const { acceptedFiles, fileRejections } = e.detail;
		fileNotAccepted = fileRejections;
		if (!acceptedFiles.length) return;
		await Promise.all(
			acceptedFiles.map((file) =>
				addUploadFile(file, file.name, file.type),
			),
		);
		handleChange(question);
	};

	const addUploadFile = async (file, name, type) => {
		value ??= {};
		const docData = getDocData(await blobToUploadData(file, type, name));
		value[docData.name] = docData;
	};

	const loadFile = async (file) => {
		if (!file) return;
		value = {};
		if (file.dataUrl) {
			value[file.name] = file;
			return;
		}
		const url =
			file.downloadUrl ||
			file.imageUrl ||
			file.thumbDownloadUrl ||
			file.thumbnailUrl;
		if (!url) return;
		const response = await fetch(url, { mode: "cors" });
		if (!response.ok) return;
		const blob = await response.blob();
		if (!blob.type.startsWith("image/")) return;
		await addUploadFile(
			blob,
			file.name ||
				decodeURIComponent(
					url.split("/").pop()?.split("?").at(0) || "image.jpg",
				),
			blob.type || file.type || "image/jpeg",
		);
	};
	$: watchObject(
		loadFile,
		`${slugify(docId)}-${slugify(key)}-FileUploads-loadFile`,
	);

	const getDocData = ({ dataUrl, size, width, height, type, name }) => ({
		name,
		type,
		size,
		formattedSize: formatFileSize(size),
		dataUrl,
		...(width && height ? { width, height } : {}),
	});

	const blobToUploadData = async (
		blob,
		type,
		name,
		maxWidth = 1600,
		quality = 0.8,
	) =>
		resizeUploadDataUrl(
			await blobToDataUrl(blob),
			type,
			name,
			maxWidth,
			quality,
		);

	const blobToDataUrl = (blob) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});

	const resizeUploadDataUrl = (dataUrl, type, name, maxWidth, quality) =>
		type?.startsWith("image/") && type !== "image/svg+xml"
			? resizeImageDataUrl(dataUrl, name, maxWidth, quality)
			: {
					dataUrl,
					name: name.split("/").pop(),
					size: getSizeInBytes(dataUrl),
					type,
				};

	const resizeImageDataUrl = (dataUrl, name, maxWidth, quality) =>
		new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => {
				const scale = Math.min(1, maxWidth / img.width);
				const width = img.width * scale;
				const height = img.height * scale;
				const canvas = document.createElement("canvas");
				canvas.width = width;
				canvas.height = height;
				canvas.getContext("2d").drawImage(img, 0, 0, width, height);
				const resizedDataUrl = canvas.toDataURL("image/jpeg", quality);
				resolve({
					dataUrl: resizedDataUrl,
					width,
					height,
					size: getSizeInBytes(resizedDataUrl),
					type: "image/jpeg",
					name: `${name.split("/").pop().split(".").slice(0, -1).join(".")}.jpg`,
				});
			};
			img.onerror = reject;
			img.src = dataUrl;
		});

	const deleteFile = (e, question, file) => {
		e.preventDefault();
		value[file] = undefined;
		delete value[file];
		if (Object.keys(value).length === 0) {
			value = undefined;
		}
		// formData = { ...formData };
		if (action && existingDoc) {
			const updates = JSON.parse(JSON.stringify(existingDoc));
			if (updates[key]?.[file]) delete updates[key][file];
			action(col, docId, undefined, updates, false, key);
		} else {
			handleChange(question);
		}
	};

	const onProgress = (uploaded, total) => {
		progress = (uploaded / total) * 100;
	};

	$: disabled =
		Object.values(value || {}).filter((v) => !v.uploaded).length === 0 ||
		loading;

	const handleUpload = async () => {
		loading = true;
		console.log("upload files");
		const responses = { [key]: value, submissionId: docId };
		const sections = [
			{ questions: [{ type: { value: "file" }, label: { value: key } }] },
		];
		console.log({ col, docId, key, value, responses, sections });
		const formData = await uploadFiles(
			responses,
			sections,
			onProgress,
			col,
		);
		console.log({ formData });
		const files = formData[key];
		const updates = { [key]: files };
		action(col, docId, key, updates, undefined, key);
		loading = false;
		progress = 0;
	};

	const resolveFileHref = (file) => {
		if (file?.href) return file.href;
		const filePath = file?.path;
		if (filePath) return `/admin/?path=${encodeURIComponent(filePath)}`;
		if (file?.downloadUrl) return file.downloadUrl;
		if (file?.thumbDownloadUrl) return file.thumbDownloadUrl;
		if (file?.shortUrl) return file.shortUrl;
		return "#";
	};

	$: limit = key?.endsWith(".value") ? 1 : question?.limit?.value;
	$: canAdd = limit === undefined || Object.keys(value || {}).length < limit;
</script>

{#if !readonly}
	{#if canAdd}
		<Dropzone
			on:drop={(e) => handleFilesSelect(e, question)}
			id={`${slugify(label || key)}-image-dropzone-component`}
			class="bg-base-300 rounded-xl p-8 flex flex-col justify-center items-center cursor-pointer"
			accept={accept ?? "image/*"}
			multiple
		/>
	{/if}
	{#if fileNotAccepted}
		{#each fileNotAccepted as error}
			<div class="text-error">
				<div>
					{error.file.type} File type not accepted - {error.file.name}
				</div>
				<div class="ml-2 sm:ml-4">
					{#each error.errors as error}
						<div>- {error.message}</div>
					{/each}
				</div>
			</div>
		{/each}
	{/if}
{/if}
<ol>
	{#each Object.keys(value || {}).sort() as file}
		<li class="my-2">
			<div class="flex gap-2 items-center justify-between">
				<div
					class="bg-base-300 rounded-xl w-full flex justify-between items-center p-2"
				>
					<!-- <a class="flex gap-2 items-center" href={value[file]?.dataUrl} target="_blank"> -->
					<div class="flex gap-2 items-center">
						<div>
							<a
								class="hover-ripple inline-block"
								href={resolveFileHref(value[file])}
								target="_blank"
								rel="noopener noreferrer"
							>
								<ImageComponent
									image={value[file]}
									class={`${value[file]?.width > value[file]?.height ? "max-w-16" : "max-w-8"} border border-current rounded`}
									preview={true}
									iconSize="3x"
								/>
							</a>
						</div>
						<div>
							<div>
								{value[file]?.name}
							</div>
							{#if !excludePath}
								<div>
									{value[file]?.path}
								</div>
							{/if}
							<div class="flex gap-2">
								<div>
									{value[file]?.type}
								</div>
								<div>
									{value[file]?.formattedSize}
								</div>
								<div>
									{#if value[file]?.width && value[file]?.height}
										{value[file]?.width} x {value[file]
											?.height}
									{/if}
								</div>
							</div>
						</div>
					</div>
					<div>
						{value[file]?.formattedSize}
					</div>
				</div>
				{#if !readonly}
					<div>
						<button
							on:click|preventDefault={(e) => {
								deleteFile(e, question, file);
							}}
							class=" text-red-700 hover-ripple p-2 btn"
						>
							<SvelteFa icon={faTrashAlt} size="1.5x" />
						</button>
					</div>
				{/if}
			</div>
		</li>
	{/each}
</ol>
{#if action}
	<div class="flex justify-between gap-2 items-center">
		<button
			{disabled}
			class={`btn btn-primary btn-sm`}
			on:click|preventDefault={handleUpload}
		>
			<span class={`text-lg sm:text-xl ${loading ? "loading" : ""}`}
			></span>
			<span> Upload Files</span>
		</button>
		<div
			class={`block m-2 ${progress === 0 ? "hidden" : ""} w-full`}
			id={slugify(
				`${slugify(col)}-${slugify(docId)}-${slugify(key)}-progress`,
			)}
		>
			<div class="text-center">uploading...</div>
			<progress
				id={slugify(
					`${slugify(col)}}-${slugify(docId)}-${slugify(key)}-progressBar`,
				)}
				class="progress"
				value={progress}
				max="100"
			></progress>
		</div>
	</div>
{/if}
